/**
 * timezoneAdapter — la liste servie par `GET api/timezones`, et le fuseau du compte, en ce que
 * l'écran « Fuseau horaire » affiche.
 *
 * La ville se lit sur le libellé FRANÇAIS (« Asie/Dubaï » → « Dubaï ») : celui de la liste
 * quand elle est chargée (mode manuel), sinon celui que le compte porte (`timezone_label`),
 * et l'identifiant IANA en dernier recours (« Asia/Dubai » → « Dubai »).
 */
import { timezoneCity } from "../../../../../data/utility/timezones"
import { type UITimezoneOption } from "../../../ui/types"

/** Une ligne de `GET api/timezones`, telle que le backend la sert. */
export type ApiTimezone = { id?: string | null; label?: string | null }

export function toUITimezoneOptions(raw: unknown): UITimezoneOption[] {
    if (!Array.isArray(raw)) return []
    return (raw as ApiTimezone[])
        .map((row): UITimezoneOption | null => (row?.id ? toUITimezoneOption(row.id, row.label) : null))
        .filter((option): option is UITimezoneOption => option != null)
}

function toUITimezoneOption(id: string, label?: string | null): UITimezoneOption {
    const text = label || id
    const segments = text.split("/")
    return {
        id,
        label: text,
        city: timezoneCity(text),
        region: segments.slice(0, -1).join("/").replace(/_/g, " "),
    }
}

/** Le fuseau du compte, écrit : la ville et le libellé entier. `null` quand le compte n'en a pas. */
export function toCurrentTimezone(
    timezone: string | null | undefined,
    options: UITimezoneOption[],
    accountLabel?: string | null
): { city: string; label: string } | null {
    if (!timezone) return null
    const known = options.find((option) => option.id === timezone) ?? toUITimezoneOption(timezone, accountLabel)
    return { city: known.city, label: known.label }
}
